import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

function createPetModel() {
    const root = new THREE.Group();
    const bodyGroup = new THREE.Group();
    const headGroup = new THREE.Group();
    const limbs = [];

    const fur = new THREE.MeshStandardMaterial({
        color: 0xd98d5f,
        roughness: 0.7,
        metalness: 0
    });

    const furDark = new THREE.MeshStandardMaterial({
        color: 0x8c5140,
        roughness: 0.78
    });

    const cream = new THREE.MeshStandardMaterial({
        color: 0xffd7ae,
        roughness: 0.82
    });

    const black = new THREE.MeshStandardMaterial({
        color: 0x16151a,
        roughness: 0.5
    });

    const pink = new THREE.MeshStandardMaterial({
        color: 0xee8e9c,
        roughness: 0.7
    });

    const body = new THREE.Mesh(
        new THREE.SphereGeometry(1, 24, 18),
        fur
    );

    body.scale.set(1.12, 1.35, 0.95);
    body.position.y = 0.15;
    body.castShadow = true;

    bodyGroup.add(body);

    const belly = new THREE.Mesh(
        new THREE.SphereGeometry(0.68, 20, 14),
        cream
    );

    belly.scale.set(0.88, 1.08, 0.35);
    belly.position.set(0, 0.05, 0.78);

    bodyGroup.add(belly);

    const head = new THREE.Mesh(
        new THREE.SphereGeometry(0.92, 24, 18),
        fur
    );

    head.scale.set(1.08, 0.96, 0.95);
    head.castShadow = true;

    headGroup.add(head);

    const muzzle = new THREE.Mesh(
        new THREE.SphereGeometry(0.45, 18, 12),
        cream
    );

    muzzle.scale.set(1.1, 0.75, 0.65);
    muzzle.position.set(0, -0.17, 0.7);

    headGroup.add(muzzle);

    const nose = new THREE.Mesh(
        new THREE.SphereGeometry(0.14, 14, 10),
        black
    );

    nose.scale.set(1.15, 0.8, 0.8);
    nose.position.set(0, -0.09, 1.02);

    headGroup.add(nose);

    const earGeometry = new THREE.ConeGeometry(0.36, 0.85, 5);

    const leftEar = new THREE.Mesh(
        earGeometry,
        furDark
    );

    leftEar.position.set(-0.52, 0.7, -0.02);
    leftEar.rotation.z = -0.18;
    leftEar.rotation.x = -0.08;

    headGroup.add(leftEar);

    const rightEar = leftEar.clone();

    rightEar.position.x = 0.52;
    rightEar.rotation.z = 0.18;

    headGroup.add(rightEar);

    const eyeGeometry = new THREE.SphereGeometry(
        0.13,
        14,
        10
    );

    const leftEye = new THREE.Mesh(
        eyeGeometry,
        black
    );

    leftEye.position.set(-0.31, 0.14, 0.82);
    leftEye.scale.y = 1.15;

    headGroup.add(leftEye);

    const rightEye = leftEye.clone();

    rightEye.position.x = 0.31;

    headGroup.add(rightEye);

    const blushGeometry = new THREE.SphereGeometry(
        0.13,
        12,
        8
    );

    const leftBlush = new THREE.Mesh(
        blushGeometry,
        pink
    );

    leftBlush.position.set(-0.53, -0.18, 0.75);
    leftBlush.scale.set(1.4, 0.55, 0.32);

    headGroup.add(leftBlush);

    const rightBlush = leftBlush.clone();

    rightBlush.position.x = 0.53;

    headGroup.add(rightBlush);

    const limbGeometry = new THREE.CapsuleGeometry(
        0.18,
        0.62,
        5,
        10
    );

    const makeLimb = (x, y, rotation) => {
        const limb = new THREE.Mesh(
            limbGeometry,
            furDark
        );

        limb.position.set(x, y, 0.03);
        limb.rotation.z = rotation;
        limb.castShadow = true;

        bodyGroup.add(limb);
        limbs.push(limb);
    };

    makeLimb(-0.88, 0.16, 0.55);
    makeLimb(0.88, 0.16, -0.55);
    makeLimb(-0.48, -0.92, 0.12);
    makeLimb(0.48, -0.92, -0.12);

    const tailCurve = new THREE.CatmullRomCurve3([
        new THREE.Vector3(0.75, -0.25, -0.35),
        new THREE.Vector3(1.35, -0.12, -0.72),
        new THREE.Vector3(1.62, 0.45, -0.38),
        new THREE.Vector3(1.35, 0.86, -0.08)
    ]);

    const tail = new THREE.Mesh(
        new THREE.TubeGeometry(
            tailCurve,
            20,
            0.24,
            8,
            false
        ),
        furDark
    );

    bodyGroup.add(tail);

    headGroup.position.y = 1.55;

    root.add(bodyGroup, headGroup);
    root.scale.setScalar(1.08);

    return {
        root,
        bodyGroup,
        headGroup,
        limbs,
        leftEye,
        rightEye,
        leftBlush,
        rightBlush,
        materials: {
            fur,
            furDark,
            cream
        }
    };
}

export default function PetScene({
                                     mood,
                                     reactionId
                                 }) {
    const hostRef = useRef(null);

    const stateRef = useRef({
        mood,
        reactionId
    });

    useEffect(() => {
        stateRef.current = {
            mood,
            reactionId
        };
    }, [mood, reactionId]);

    useEffect(() => {
        const host = hostRef.current;

        if (!host) {
            return undefined;
        }

        const scene = new THREE.Scene();

        const camera = new THREE.PerspectiveCamera(
            32,
            1,
            0.1,
            100
        );

        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance'
        });

        renderer.setPixelRatio(
            Math.min(
                window.devicePixelRatio,
                2
            )
        );

        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type =
            THREE.PCFSoftShadowMap;

        renderer.outputColorSpace =
            THREE.SRGBColorSpace;

        host.appendChild(renderer.domElement);

        const hemi = new THREE.HemisphereLight(
            0xfff0df,
            0x3a4059,
            2.8
        );

        scene.add(hemi);

        const key = new THREE.DirectionalLight(
            0xffd7b8,
            4.3
        );

        key.position.set(4, 6, 5);
        key.castShadow = true;

        scene.add(key);

        const rim = new THREE.PointLight(
            0x8db9ff,
            4.5,
            12
        );

        rim.position.set(-4, 2, 2);

        scene.add(rim);

        const platform = new THREE.Mesh(
            new THREE.CylinderGeometry(
                2.35,
                2.65,
                0.42,
                48
            ),
            new THREE.MeshStandardMaterial({
                color: 0x242836,
                roughness: 0.9,
                metalness: 0.05
            })
        );

        platform.position.y = -1.58;
        platform.receiveShadow = true;

        scene.add(platform);

        const ring = new THREE.Mesh(
            new THREE.TorusGeometry(
                1.82,
                0.028,
                8,
                80
            ),
            new THREE.MeshBasicMaterial({
                color: 0x6d7bff,
                transparent: true,
                opacity: 0.46
            })
        );

        ring.rotation.x = Math.PI / 2;
        ring.position.y = -1.35;

        scene.add(ring);

        const pet = createPetModel();

        pet.root.position.y = -0.35;

        scene.add(pet.root);

        const clock = new THREE.Clock();

        let frame = 0;
        let drag = false;
        let lastX = 0;
        let targetRotation = 0;
        let currentReaction = reactionId;
        let reactionStarted = 0;

        const fitPetCamera = margin => {
            pet.root.updateMatrixWorld(true);

            const box = new THREE.Box3().setFromObject(
                pet.root
            );

            const size = box.getSize(
                new THREE.Vector3()
            );

            const center = box.getCenter(
                new THREE.Vector3()
            );

            const verticalFov =
                THREE.MathUtils.degToRad(
                    camera.fov
                );

            const horizontalFov =
                2 *
                Math.atan(
                    Math.tan(verticalFov / 2) *
                    camera.aspect
                );

            const verticalDistance =
                size.y /
                (2 * Math.tan(verticalFov / 2));

            const horizontalDistance =
                size.x /
                (2 * Math.tan(horizontalFov / 2));

            const distance =
                Math.max(
                    verticalDistance,
                    horizontalDistance
                ) * margin;

            camera.position.set(
                center.x,
                center.y,
                center.z + distance
            );

            camera.lookAt(center);
        };

        const resize = () => {
            const width = Math.max(
                host.clientWidth,
                1
            );

            const height = Math.max(
                host.clientHeight,
                1
            );

            const viewportWidth =
                window.innerWidth;

            renderer.setSize(
                width,
                height,
                false
            );

            camera.aspect =
                width / height;

            if (viewportWidth <= 600) {
                fitPetCamera(1.55);
            } else if (viewportWidth <= 900) {
                fitPetCamera(1.3);
            } else {
                camera.position.set(
                    0,
                    1.45,
                    8.7
                );

                camera.rotation.set(
                    0,
                    0,
                    0
                );
            }

            camera.updateProjectionMatrix();
        };

        const pointerDown = event => {
            drag = true;
            lastX = event.clientX;

            renderer.domElement.setPointerCapture?.(
                event.pointerId
            );
        };

        const pointerMove = event => {
            if (!drag) {
                return;
            }

            const delta =
                event.clientX - lastX;

            lastX = event.clientX;

            targetRotation +=
                delta * 0.012;
        };

        const pointerUp = () => {
            drag = false;
        };

        renderer.domElement.addEventListener(
            'pointerdown',
            pointerDown
        );

        renderer.domElement.addEventListener(
            'pointermove',
            pointerMove
        );

        renderer.domElement.addEventListener(
            'pointerup',
            pointerUp
        );

        renderer.domElement.addEventListener(
            'pointercancel',
            pointerUp
        );

        window.addEventListener(
            'resize',
            resize
        );

        const resizeObserver =
            new ResizeObserver(() => {
                resize();
            });

        resizeObserver.observe(host);

        resize();

        const animate = () => {
            frame =
                requestAnimationFrame(
                    animate
                );

            const t =
                clock.getElapsedTime();

            const current =
                stateRef.current;

            if (
                current.reactionId !==
                currentReaction
            ) {
                currentReaction =
                    current.reactionId;

                reactionStarted = t;
            }

            const reactionTime =
                t - reactionStarted;

            const activeReaction =
                reactionTime < 1.3;

            const moodNow =
                current.mood;

            const idleSpeed =
                moodNow === 'happy'
                    ? 2.4
                    : moodNow === 'sad' ||
                    moodNow === 'sick'
                        ? 1.2
                        : 1.8;

            const bob =
                Math.sin(
                    t * idleSpeed
                ) *
                (
                    moodNow === 'happy'
                        ? 0.07
                        : 0.035
                );

            pet.root.position.y =
                -0.35 + bob;

            pet.root.rotation.y +=
                (
                    targetRotation -
                    pet.root.rotation.y
                ) * 0.08;

            pet.bodyGroup.rotation.z =
                Math.sin(
                    t * 1.35
                ) * 0.025;

            pet.headGroup.rotation.z =
                Math.sin(
                    t * 1.15
                ) * 0.045;

            pet.headGroup.rotation.x =
                moodNow === 'sad' ||
                moodNow === 'sick'
                    ? 0.18
                    : -0.03;

            pet.limbs[0].rotation.z =
                0.55 +
                Math.sin(
                    t * 1.4
                ) * 0.05;

            pet.limbs[1].rotation.z =
                -0.55 -
                Math.sin(
                    t * 1.4
                ) * 0.05;

            const blinkPhase =
                t % 4.6;

            const blink =
                blinkPhase > 4.42
                    ? 0.08
                    : 1;

            pet.leftEye.scale.y =
                blink;

            pet.rightEye.scale.y =
                blink;

            if (activeReaction) {
                const pulse =
                    Math.sin(
                        Math.min(
                            1,
                            reactionTime / 1.1
                        ) * Math.PI
                    );

                pet.root.position.y +=
                    pulse * 0.65;

                pet.root.rotation.z =
                    Math.sin(
                        reactionTime * 12
                    ) *
                    0.05 *
                    pulse;

                pet.headGroup.rotation.x -=
                    pulse * 0.12;
            } else {
                pet.root.rotation.z *=
                    0.9;
            }

            if (moodNow === 'hungry') {
                pet.bodyGroup.scale.setScalar(
                    0.97 +
                    Math.sin(
                        t * 5
                    ) * 0.01
                );
            } else {
                pet.bodyGroup.scale.setScalar(1);
            }

            const dead =
                moodNow === 'dead';

            pet.materials.fur.color.setHex(
                dead
                    ? 0x756f74
                    : 0xd98d5f
            );

            pet.materials.furDark.color.setHex(
                dead
                    ? 0x4d494d
                    : 0x8c5140
            );

            pet.materials.cream.color.setHex(
                dead
                    ? 0xaba1a0
                    : 0xffd7ae
            );

            pet.leftBlush.visible =
                !dead &&
                moodNow === 'happy';

            pet.rightBlush.visible =
                !dead &&
                moodNow === 'happy';

            pet.root.rotation.x =
                dead
                    ? 0.35
                    : 0;

            pet.root.position.y +=
                dead
                    ? -0.65
                    : 0;

            ring.rotation.z +=
                0.0025;

            renderer.render(
                scene,
                camera
            );
        };

        animate();

        return () => {
            cancelAnimationFrame(frame);

            resizeObserver.disconnect();

            window.removeEventListener(
                'resize',
                resize
            );

            renderer.domElement.removeEventListener(
                'pointerdown',
                pointerDown
            );

            renderer.domElement.removeEventListener(
                'pointermove',
                pointerMove
            );

            renderer.domElement.removeEventListener(
                'pointerup',
                pointerUp
            );

            renderer.domElement.removeEventListener(
                'pointercancel',
                pointerUp
            );

            renderer.dispose();

            renderer.domElement.remove();

            scene.traverse(object => {
                if (object.geometry) {
                    object.geometry.dispose();
                }

                if (object.material) {
                    const materials =
                        Array.isArray(
                            object.material
                        )
                            ? object.material
                            : [
                                object.material
                            ];

                    materials.forEach(
                        material =>
                            material.dispose()
                    );
                }
            });
        };
    }, []);

    return (
        <div
            className="pet-scene"
            ref={hostRef}
            aria-label="Interaktivní 3D mazlíček"
        />
    );
}